import { createSlice, createAsyncThunk, isAnyOf } from '@reduxjs/toolkit';
import { showToast } from 'Core/redux/toast/slice';
import { closeModal, showModal } from 'Core/redux/main/slice';
import { redux } from 'Core/redux';
import { MODAL_TYPES } from 'Feature/components/app-modals';
const { CIPREGISTER_ADD_EDIT } = MODAL_TYPES;

export const STORE_KEY = 'CipRegister';
export const INITIAL_STATE = {
  data: [],
  loading: false,
  fetchLoading: false,
  error: null,
  filters: {
    searchText: '',
    status: null,
  },
  filterVisible: false,
  tileView: true,
};

// ── Async Thunks (UI-only stubs – wire to real API service later) ─────────────

export const fetchCipRegisters = createAsyncThunk(
  'CipRegister/fetchCipRegisters',
  async (_, { rejectWithValue }) => {
    try {
      // TODO: replace with real API call, e.g. CipRegisterService.getAll()
      return [
        {
          id: 1,
          crNumber: 'CIP-2026-014',
          title: 'Audit Finding - Document Control',
          status: 'Verification',
          initiator: 'Quality Team',
          cipNumber: 'CIP-2026-014',
          source: 'Audit',
          coordinator: 'Justin / Quality',
          dueDate: 'Mar 29, 2026',
        },
        {
          id: 2,
          crNumber: 'CIP-2026-011',
          title: 'Reduce Scrap on Molding Line',
          status: 'Investigation',
          initiator: 'Quality Team',
        },
        {
          id: 3,
          crNumber: 'CIP-2026-009',
          title: 'Supplier Packaging Improvement',
          status: 'Validation',
          initiator: 'Quality Team',
        },
        {
          id: 4,
          crNumber: 'CIP-2026-006',
          title: 'Customer Label Accuracy',
          status: 'Closed',
          initiator: 'Quality Team',
        },
        {
          id: 5,
          crNumber: 'CIP-2026-002',
          title: '5S in Warehouse Area',
          status: 'Draft',
          initiator: 'Quality Team',
        },
      ];
    } catch (error) {
      return rejectWithValue(error.message || 'Failed to load CIP Registers');
    }
  },
);

export const fetchCipRegisterById = createAsyncThunk(
  'CipRegister/fetchCipRegisterById',
  async (id, { rejectWithValue, getState }) => {
    try {
      const state = getState();
      const item = (state[STORE_KEY]?.data || []).find(cr => cr.id === id);
      if (item) return item;
      return rejectWithValue('CIP Register not found');
    } catch (error) {
      return rejectWithValue(error.message || 'Failed to load CIP Register');
    }
  },
);

export const addCipRegister = createAsyncThunk(
  'CipRegister/addCipRegister',
  async (payload, { rejectWithValue }) => {
    try {
      // TODO: replace with real API call
      return {
        ...payload,
        id: Date.now(),
        crNumber: `CR-${Math.floor(1000 + Math.random() * 9000)}`,
        isNewlyCreated: true,
      };
    } catch (error) {
      return rejectWithValue(error.message || 'Failed to add CIP Register');
    }
  },
);

export const editCipRegister = createAsyncThunk(
  'CipRegister/editCipRegister',
  async (payload, { rejectWithValue }) => {
    try {
      // TODO: replace with real API call
      return payload;
    } catch (error) {
      return rejectWithValue(error.message || 'Failed to update CIP Register');
    }
  },
);

export const deleteCipRegister = createAsyncThunk(
  'CipRegister/deleteCipRegister',
  async (id, { rejectWithValue }) => {
    try {
      // TODO: replace with real API call
      return id;
    } catch (error) {
      return rejectWithValue(error.message || 'Failed to delete CIP Register');
    }
  },
);

// ── Slice ─────────────────────────────────────────────────────────────────────

export const { reducer, actions } = createSlice({
  name: STORE_KEY,
  initialState: INITIAL_STATE,
  reducers: {
    setFilters: (state, action) => {
      state.filters = action.payload;
    },
    resetFilters: state => {
      state.filters = INITIAL_STATE.filters;
    },
    setFilterVisible: (state, action) => {
      state.filterVisible = action.payload;
    },
    setSearchText: (state, action) => {
      state.filters.searchText = action.payload;
    },
    setStatus: (state, action) => {
      state.filters.status = action.payload;
    },
    setTileView: (state, action) => {
      state.tileView = action.payload;
    },
  },
  extraReducers: builder => {
    builder
      .addCase(fetchCipRegisters.pending, state => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchCipRegisters.fulfilled, (state, action) => {
        state.loading = false;
        state.data = action.payload;
      })
      .addCase(fetchCipRegisters.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      .addCase(fetchCipRegisterById.pending, state => {
        state.fetchLoading = true;
      })
      .addCase(fetchCipRegisterById.fulfilled, state => {
        state.fetchLoading = false;
      })
      .addCase(fetchCipRegisterById.rejected, state => {
        state.fetchLoading = false;
      })
      .addCase(addCipRegister.fulfilled, (state, action) => {
        state.data = [action.payload, ...state.data];
      })
      .addCase(addCipRegister.rejected, (state, action) => {
        state.error = action.payload;
      })
      .addCase(editCipRegister.fulfilled, (state, action) => {
        const idx = state.data.findIndex(cr => cr.id === action.payload.id);
        if (idx !== -1) state.data[idx] = action.payload;
      })
      .addCase(editCipRegister.rejected, (state, action) => {
        state.error = action.payload;
      })
      .addCase(deleteCipRegister.fulfilled, (state, action) => {
        state.data = state.data.filter(cr => cr.id !== action.payload);
      });
  },
});

// ── Listener Middleware ───────────────────────────────────────────────────────

redux.listener.startListening({
  matcher: isAnyOf(addCipRegister.rejected, editCipRegister.rejected),
  effect: (action, api) => {
    api.dispatch(showToast({ message: action.payload, type: 'danger' }));
  },
});

redux.listener.startListening({
  matcher: isAnyOf(addCipRegister.fulfilled, editCipRegister.fulfilled),
  effect: (_, api) => {
    api.dispatch(closeModal(CIPREGISTER_ADD_EDIT));
    api.dispatch(showToast({ message: 'CIP Register saved successfully', type: 'success' }));
  },
});

redux.listener.startListening({
  matcher: isAnyOf(fetchCipRegisterById.fulfilled),
  effect: (action, api) => {
    api.dispatch(showModal(CIPREGISTER_ADD_EDIT, action.payload));
  },
});

redux.listener.startListening({
  matcher: isAnyOf(deleteCipRegister.fulfilled),
  effect: (_, api) => {
    api.dispatch(showToast({ message: 'CIP Register deleted successfully', type: 'success' }));
  },
});

redux.injectReducer(STORE_KEY, reducer);
